import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import {
  getEventWeb,
  initOrganizerEvents,
} from "../../../../data-store/actions/organizers-actions";
import { Grid, Typography } from "@material-ui/core";
import TablePagination from "@material-ui/core/TablePagination";

import NextEventsCard from "./Next/NextEventsCard";
import PastEventsCard from "./Past/PastEventsCard";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },

  monthDivider: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },
  monthLine: {
    flex: 1,
    height: 1,
    backgroundColor: theme.palette.divider,
  },
  monthTitle: {
    margin: theme.spacing(0, 2),
    fontWeight: 700,
    fontSize: 24,
    lineHeight: 1.2,
    textTransform: "uppercase",
    color: theme.palette.text.primary,
    backgroundColor: "transparent",
    letterSpacing: 0.8,
  },

  paginationWrap: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
}));

export default function EventsList(props) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { eventStatus, businessId, title, typeView, typeList, pagination } =
    props.componentConfig;

  const organizerEvents = useSelector(
    (state) => state.organizer.organizerEvents
  );

  const [page, setPage] = useState(0);
  const listTopRef = useRef(null);

  useEffect(() => {
    dispatch(initOrganizerEvents(businessId, eventStatus));
  }, [eventStatus, businessId]);

  useEffect(() => {
    setPage(0);
  }, [eventStatus, businessId, typeList, pagination]);

  const parseFechaEvento = (fechaEvento) => {
    if (!fechaEvento) return null;

    // Si viene como YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(fechaEvento)) {
      const [year, month, day] = fechaEvento.split("-").map(Number);
      return new Date(year, month - 1, day);
    }

    // Si viene como YYYY/MM/DD HH:mm:ss o similar
    if (/^\d{4}\/\d{2}\/\d{2}/.test(fechaEvento)) {
      return new Date(fechaEvento);
    }

    return new Date(fechaEvento);
  };

  function eventsFbToArray(events, status) {
    const eventosArray = [];

    for (var i in events) {
      if (events[i].eventoDisponible / 1 === status) {
        eventosArray.push(events[i]);
      }
    }

    if (eventStatus === 1) {
      eventosArray.sort(
        (a, b) => new Date(b.fechaEvento) - new Date(a.fechaEvento)
      );
    } else {
      eventosArray.sort(
        (a, b) => new Date(a.fechaEvento) - new Date(b.fechaEvento)
      );
    }

    return eventosArray;
  }

  const renderCard = (evento) => {
    if (eventStatus === 2) {
      return <NextEventsCard evento={evento} typeView={typeView} />;
    }
    return <PastEventsCard evento={evento} typeView={typeView} />;
  };

  const eventosOrdenados = useMemo(() => {
    const arr = eventsFbToArray(organizerEvents, eventStatus).filter(
      (e) => e.fechaEvento
    );

    arr.sort((a, b) => {
      const fa = parseFechaEvento(a.fechaEvento);
      const fb = parseFechaEvento(b.fechaEvento);
      return eventStatus === 1 ? fb - fa : fa - fb;
    });

    return arr;
  }, [organizerEvents, eventStatus]);

  const total = eventosOrdenados.length;

  const getRowsPerPageByConfig = () => {
    if (pagination === 1 || pagination === "1") return 20;
    if (pagination === 2 || pagination === "2") return 40;
    if (pagination === 3 || pagination === "3") return 60;
    return total || 1;
  };

  const rowsPerPage = getRowsPerPageByConfig();
  const usePagination =
    (pagination === 1 ||
      pagination === "1" ||
      pagination === 2 ||
      pagination === "2" ||
      pagination === 3 ||
      pagination === "3") && total > rowsPerPage;

  useEffect(() => {
    if (!usePagination) {
      setPage(0);
      return;
    }
    const maxPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [total, rowsPerPage, usePagination]);

  const start = usePagination ? page * rowsPerPage : 0;
  const end = usePagination ? start + rowsPerPage : total;
  const eventosPagina = usePagination
    ? eventosOrdenados.slice(start, end)
    : eventosOrdenados;

  const scrollToListTop = () => {
    if (
      listTopRef.current &&
      typeof listTopRef.current.scrollIntoView === "function"
    ) {
      listTopRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleChangePage = (e, newPage) => {
    setPage(newPage);
    requestAnimationFrame(scrollToListTop);
  };

  const Pagination = () =>
    usePagination ? (
      <div className={classes.paginationWrap}>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={() => { }}
          rowsPerPageOptions={[]}
          labelRowsPerPage=""
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </div>
    ) : null;

  if (typeList === "1") {
    let lastMonthYear = null;

    if (usePagination && start > 0 && eventosOrdenados[start - 1]?.fechaEvento) {
      const prev = parseFechaEvento(eventosOrdenados[start - 1].fechaEvento);
      lastMonthYear = `${prev.getFullYear()}-${prev.getMonth()}`;
    }

    return (
      <div>
        <Typography variant="h3" color="primary" style={{ textAlign: "center" }}>
          {title}
        </Typography>

        <Pagination />

        <div ref={listTopRef} />

        <Grid container direction="row" justify="left" alignItems="left" spacing={2}>
          {eventosPagina.map((evento) => {
            const fecha = parseFechaEvento(evento.fechaEvento);
            const monthYearKey = `${fecha.getFullYear()}-${fecha.getMonth()}`;
            const showTitle = monthYearKey !== lastMonthYear;
            lastMonthYear = monthYearKey;

            const monthLabel = fecha.toLocaleDateString("es-MX", {
              month: "long",
              year: "numeric",
            });

            return (
              <React.Fragment key={evento.iDEvento}>
                {showTitle && (
                  <Grid item xs={12}>
                    <div className={classes.monthDivider}>
                      <div className={classes.monthLine} />
                      <Typography className={classes.monthTitle}>
                        {monthLabel}
                      </Typography>
                      <div className={classes.monthLine} />
                    </div>
                  </Grid>
                )}

                <Grid item xs={12} sm={4}>
                  {renderCard(evento)}
                </Grid>
              </React.Fragment>
            );
          })}
        </Grid>

        <Pagination />
      </div>
    );
  }

  return (
    <div>
      <Typography variant="h3" color="primary" style={{ textAlign: "center" }}>
        {title}
      </Typography>

      <Pagination />

      <div ref={listTopRef} />

      <Grid container direction="row" justify="left" alignItems="left" spacing={2}>
        {eventosPagina.map((evento) => (
          <Grid item xs={12} sm={4} key={evento.iDEvento}>
            {renderCard(evento)}
          </Grid>
        ))}
      </Grid>

      <Pagination />
    </div>
  );
}

EventsList.displayName = "Lista de Eventos";

export const componentDescription = {
  componentConfig: {
    title: {
      label: "Titulo de la sección",
      errorMessage: "Campo Requerido",
      fieldName: "title",
      type: "text",
      defaultValue: "",
      required: true,
      regex: "",
      textSize: "h6",
    },
    eventStatus: {
      label: "Status de los eventos a mostrar",
      errorMessage: "Campo Requerido",
      fieldName: "eventStatus",
      type: "select",
      defaultValue: "",
      required: true,
      regex: "",
      textSize: "h6",
      optionsArray: [
        { value: 2, label: "En proceso de inscripciones" },
        { value: 4, label: "Durante el evento" },
        { value: 1, label: "Resultados publicados" },
      ],
    },
    businessId: {
      label: "Id del negocio",
      errorMessage: "Campo Requerido",
      fieldName: "businessId",
      type: "text",
      defaultValue: "",
      required: true,
      regex: "",
      textSize: "h6",
    },
    typeList: {
      label: "Tipo de vista para la lista",
      errorMessage: "Campo Requerido",
      fieldName: "typeList",
      type: "select",
      defaultValue: "standar",
      required: true,
      regex: "",
      textSize: "h6",
      optionsArray: [
        {
          value: "standar",
          label:
            "Vista estandar (una tarjeta por cada evento organizada por fecha)",
        },
        {
          value: "1",
          label:
            "Una tarjeta por cada evento Separado por mes y año con titulo mensual",
        },
      ],
    },
    typeView: {
      label: "Tipo de vista por cada evento",
      errorMessage: "Campo Requerido",
      fieldName: "typeView",
      type: "select",
      defaultValue: "standar",
      required: true,
      regex: "",
      textSize: "h6",
      optionsArray: [
        {
          value: "standar",
          label: "Vista estandar (imagen arribo e información resumida)",
        },
        { value: "1", label: "Vista custom Chronosport" },
      ],
    },
    pagination: {
      label: "Paginado de la lista",
      errorMessage: "Campo Requerido",
      fieldName: "pagination",
      type: "select",
      defaultValue: 0,
      required: true,
      regex: "",
      textSize: "h6",
      optionsArray: [
        { value: 0, label: "Mostrar Todos sin paginado" },
        { value: 1, label: "Mostrar paginado de 20 en 20" },
        { value: 2, label: "Mostrar paginado de 40 en 40" },
        { value: 3, label: "Mostrar paginado de 60 en 60" },
      ],
    },
  },
};